import {useCallback, type Dispatch, type SetStateAction} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useDescartarCuarentena} from '@/hooks/bateria/use-bateria'
import {detalleDelError, translateApiError} from '@/lib/errors'
import type {DecisionLocal} from '@/lib/bateria-cola'

/** Trata el 409 `the page is not pending` (la hoja ya se resolvió mientras la cola la ofrecía)
 * como `conflicto` local, sin el toast genérico. */
export function useDescartarCuarentenaDeLaCola(
  businessId: string,
  evaluacionId: string,
  restaurarReset: () => void,
  setDescartada: (cuarentenaId: string) => void,
  setDecisiones: Dispatch<SetStateAction<Record<string, DecisionLocal>>>
): (vars: {cuarentenaId: string; motivo: string | null}) => void {
  const queryClient = useQueryClient()
  const descartar = useDescartarCuarentena(businessId, evaluacionId)
  const descartarMutate = descartar.mutate
  return useCallback(
    (vars: {cuarentenaId: string; motivo: string | null}) =>
      descartarMutate(vars, {
        onSuccess: () => {
          restaurarReset()
          setDescartada(vars.cuarentenaId)
        },
        onError: (err) => {
          if (detalleDelError(err) !== 'the page is not pending') return
          translateApiError(err) // marca el error comunicado; sin esto la red global muestra el toast
          const ref = `ubicar:${vars.cuarentenaId}`
          setDecisiones((previas) => {
            const local = previas[ref]
            if (!local) return previas
            return {
              ...previas,
              [ref]: {
                ...local,
                estado: 'conflicto',
                decididoPor: null,
                decididoPorNombre: null,
                decididoEn: null,
                ganador: null,
              },
            }
          })
          queryClient.invalidateQueries({queryKey: ['bateria-cola', businessId, evaluacionId]})
        },
      }),
    [descartarMutate, restaurarReset, setDescartada, setDecisiones, queryClient, businessId, evaluacionId]
  )
}
